import React, { useEffect, useState } from "react";
import TopNav from "@/components/TopNav";
import { api } from "@/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/auth/AuthContext";
import { Navigate } from "react-router-dom";

export default function AdminUsersPage() {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === "admin";
  const isSupervisor = currentUser?.role === "supervisor";

  const [users, setUsers] = useState([]);
  const [error, setError] = useState("");
  const [driveStatus, setDriveStatus] = useState(null);

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("user");
  const [userCode, setUserCode] = useState("");
  const [creating, setCreating] = useState(false);

  // Edit user state
  const [editUser, setEditUser] = useState(null);
  const [editName, setEditName] = useState("");
  const [editUserCode, setEditUserCode] = useState("");
  const [editRole, setEditRole] = useState("user");
  const [saving, setSaving] = useState(false);

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = async () => {
    setError("");
    try {
      // Supervisors only need users list, not drive status
      if (isAdmin) {
        const [uRes, dRes] = await Promise.all([
          api.get("/admin/users"),
          api.get("/admin/drive/status"),
        ]);
        setUsers(uRes.data.users || []);
        setDriveStatus(dRes.data);
      } else {
        // Supervisor - only fetch users
        const uRes = await api.get("/admin/users");
        setUsers(uRes.data.users || []);
      }
    } catch (e) {
      if (isSupervisor) {
        setError("Could not load data. You may not have any users in your assigned groups.");
      } else {
        setError("Could not load admin data. Ensure you are logged in as admin.");
      }
    }
  };

  useEffect(() => {
    if (isAdmin || isSupervisor) {
      load();
    }
  }, [isAdmin, isSupervisor]);

  // Redirect regular users
  if (!isAdmin && !isSupervisor) {
    return <Navigate to="/dashboard" replace />;
  }

  const setUserStatus = async (userId, action) => {
    setError("");
    try {
      await api.post(`/admin/users/${userId}/${action}`);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to update user");
    }
  };

  const deleteUser = async (userId, mode) => {
    // mode: 'keep' or 'purge'
    setError("");
    try {
      if (mode === "purge") {
        await api.delete(`/admin/users/${userId}/purge`);
      } else {
        await api.delete(`/admin/users/${userId}`);
      }
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to delete user");
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    setError("");
    setCreating(true);
    try {
      await api.post("/admin/users", { email, name, password, role, user_code: userCode });
      setEmail("");
      setName("");
      setPassword("");
      setRole("user");
      setUserCode("");
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to create user");
    } finally {
      setCreating(false);
    }
  };

  const openEditDialog = (user) => {
    setEditUser(user);
    setEditName(user.name || "");
    setEditUserCode(user.user_code || "");
    setEditRole(user.role || "user");
  };

  const closeEditDialog = () => {
    setEditUser(null);
    setEditName("");
    setEditUserCode("");
    setEditRole("user");
  };

  const saveUserEdit = async () => {
    if (!editUser) return;
    setSaving(true);
    setError("");
    try {
      await api.put(`/admin/users/${editUser.user_id}`, {
        name: editName,
        user_code: editUserCode,
        role: editRole,
      });
      closeEditDialog();
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to update user");
    } finally {
      setSaving(false);
    }
  };

  // Filter users based on search term and status
  const filteredUsers = users.filter((u) => {
    const matchesSearch =
      searchTerm === "" ||
      u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (u.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (u.user_code || "").toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || u.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  return (
    <div data-testid="admin-users-page" className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-100 text-slate-900">
      <TopNav />
      <div className="mx-auto max-w-6xl px-4 py-8 space-y-6">
        <div>
          <div className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight" data-testid="admin-users-title">
            {isSupervisor ? "Team Members" : "Admin"}
          </div>
          <div className="mt-2 text-base md:text-lg text-slate-600" data-testid="admin-users-subtitle">
            {isSupervisor ? "View users in your groups (read-only)." : "Approve, block, and create users."}
          </div>
        </div>

        {error ? (
          <div data-testid="admin-users-error" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">
            {error}
          </div>
        ) : null}

        {/* Admin-only section: Drive status and Create user */}
        {isAdmin && (
          <div className="grid gap-4 lg:grid-cols-3">
            <Card data-testid="admin-drive-card" className="border-slate-200 bg-white shadow-sm">
              <CardHeader>
                <CardTitle data-testid="admin-drive-title">Google Drive</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="text-sm text-slate-800" data-testid="admin-drive-configured">
                  Configured: {driveStatus ? String(driveStatus.configured) : "…"}
                </div>
                <div className="text-sm text-slate-800" data-testid="admin-drive-folder-id">
                  Folder ID: {driveStatus?.folder_id || "(not set)"}
                </div>
                <div className="text-xs text-slate-500" data-testid="admin-drive-note">
                  Uploads will be saved into your configured Shared Drive folder.
                </div>
              </CardContent>
            </Card>

            <Card data-testid="admin-create-user-card" className="border-slate-200 bg-white shadow-sm lg:col-span-2">
              <CardHeader>
                <CardTitle data-testid="admin-create-user-title">Create user (email/password)</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={createUser} className="grid gap-4 md:grid-cols-2" data-testid="admin-create-user-form">
                <div className="space-y-2">
                  <Label data-testid="admin-user-email-label" htmlFor="u-email">Email</Label>
                  <Input
                    data-testid="admin-user-email-input"
                    id="u-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pod-input-light bg-white border-slate-300"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label data-testid="admin-user-name-label" htmlFor="u-name">Name</Label>
                  <Input
                    data-testid="admin-user-name-input"
                    id="u-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Driver / Staff name"
                    className="pod-input-light bg-white border-slate-300"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label data-testid="admin-user-password-label" htmlFor="u-pass">Password</Label>
                  <Input
                    data-testid="admin-user-password-input"
                    id="u-pass"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pod-input-light bg-white border-slate-300"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label data-testid="admin-user-code-label" htmlFor="u-code">User code (for filenames)</Label>
                  <Input
                    data-testid="admin-user-code-input"
                    id="u-code"
                    value={userCode}
                    onChange={(e) => setUserCode(e.target.value)}
                    placeholder="E.g. DRIVER_07 or VEHICLE_KA01"
                    className="pod-input-light bg-white border-slate-300"
                  />
                </div>

                <div className="space-y-2">
                  <Label data-testid="admin-user-role-label">Role</Label>
                  <Select value={role} onValueChange={setRole} data-testid="admin-user-role-select">
                    <SelectTrigger data-testid="admin-user-role-trigger" className="border-slate-300 bg-white text-slate-900">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent data-testid="admin-user-role-content" className="border-slate-200 bg-white text-slate-900">
                      <SelectItem value="user" data-testid="admin-user-role-user">User</SelectItem>
                      <SelectItem value="supervisor" data-testid="admin-user-role-supervisor">Supervisor</SelectItem>
                      <SelectItem value="admin" data-testid="admin-user-role-admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="md:col-span-2 flex items-center justify-between">
                  <Button
                    data-testid="admin-create-user-submit"
                    type="submit"
                    disabled={creating}
                    className="rounded-full bg-slate-900 text-white hover:bg-slate-800"
                  >
                    {creating ? "Creating…" : "Create user"}
                  </Button>
                  <Button
                    data-testid="admin-reload-button"
                    type="button"
                    variant="secondary"
                    className="rounded-full bg-white border border-slate-300 text-slate-800 hover:bg-slate-50"
                    onClick={load}
                  >
                    Reload
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
        )}

        <Card data-testid="admin-users-list-card" className="border-slate-200 bg-white shadow-sm">
          <CardHeader>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle data-testid="admin-users-list-title">Users ({filteredUsers.length})</CardTitle>
              <div className="flex flex-wrap items-center gap-3">
                <Input
                  data-testid="admin-users-search"
                  type="text"
                  placeholder="Search by email, name, or code…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full sm:w-64 pod-input-light bg-white border-slate-300"
                />
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger data-testid="admin-users-status-filter" className="w-32 border-slate-300 bg-white text-slate-900">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent className="border-slate-200 bg-white text-slate-900">
                    <SelectItem value="all" data-testid="admin-users-filter-all">All</SelectItem>
                    <SelectItem value="active" data-testid="admin-users-filter-active">Active</SelectItem>
                    <SelectItem value="pending" data-testid="admin-users-filter-pending">Pending</SelectItem>
                    <SelectItem value="blocked" data-testid="admin-users-filter-blocked">Blocked</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredUsers.length === 0 ? (
              <div data-testid="admin-users-empty" className="text-sm text-slate-600">
                {users.length === 0 ? "No users yet." : "No users match your search/filter."}
              </div>
            ) : (
              <div className="overflow-auto" data-testid="admin-users-table-wrapper">
                <table className="w-full text-sm" data-testid="admin-users-table">
                  <thead>
                    <tr className="text-left text-slate-600">
                      <th className="py-2 pr-4" data-testid="admin-users-th-email">Email</th>
                      <th className="py-2 pr-4" data-testid="admin-users-th-name">Name</th>
                      <th className="py-2 pr-4" data-testid="admin-users-th-role">Role</th>
                      <th className="py-2 pr-4" data-testid="admin-users-th-status">Status</th>
                      <th className="py-2 pr-4" data-testid="admin-users-th-code">Code</th>
                      <th className="py-2 pr-4" data-testid="admin-users-th-providers">Providers</th>
                      {isAdmin && <th className="py-2 pr-4" data-testid="admin-users-th-actions">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((u, idx) => (
                      <tr key={u.user_id} className="border-t border-slate-200">
                        <td className="py-2 pr-4 text-slate-900" data-testid={`admin-users-email-${idx}`}>{u.email}</td>
                        <td className="py-2 pr-4 text-slate-900" data-testid={`admin-users-name-${idx}`}>{u.name}</td>
                        <td className="py-2 pr-4 text-slate-900" data-testid={`admin-users-role-${idx}`}>{u.role}</td>
                        <td className="py-2 pr-4 text-slate-900" data-testid={`admin-users-status-${idx}`}>{u.status || "active"}</td>
                        <td className="py-2 pr-4 text-slate-900" data-testid={`admin-users-code-${idx}`}>{u.user_code || "—"}</td>
                        <td className="py-2 pr-4 text-slate-600" data-testid={`admin-users-providers-${idx}`}>{(u.auth_providers || []).join(", ")}</td>
                        {isAdmin && (
                        <td className="py-2 pr-4" data-testid={`admin-users-actions-${idx}`}>
                          <div className="flex flex-wrap gap-2">
                            <Button
                              data-testid={`admin-users-edit-${idx}`}
                              type="button"
                              variant="secondary"
                              className="h-8 rounded-full bg-white border border-sky-300 text-sky-700 hover:bg-sky-50"
                              onClick={() => openEditDialog(u)}
                            >
                              Edit
                            </Button>
                            {u.status === "pending" ? (
                              <Button
                                data-testid={`admin-users-approve-${idx}`}
                                type="button"
                                className="h-8 rounded-full bg-emerald-600 text-white hover:bg-emerald-700"
                                onClick={() => setUserStatus(u.user_id, "approve")}
                              >
                                Approve
                              </Button>
                            ) : null}
                            {u.status !== "blocked" ? (
                              <Button
                                data-testid={`admin-users-block-${idx}`}
                                type="button"
                                variant="secondary"
                                className="h-8 rounded-full bg-white border border-rose-300 text-rose-700 hover:bg-rose-50"
                                onClick={() => setUserStatus(u.user_id, "block")}
                              >
                                Block
                              </Button>
                            ) : (
                              <Button
                                data-testid={`admin-users-unblock-${idx}`}
                                type="button"
                                className="h-8 rounded-full bg-slate-900 text-white hover:bg-slate-800"
                                onClick={() => setUserStatus(u.user_id, "unblock")}
                              >
                                Unblock
                              </Button>
                            )}

                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  data-testid={`admin-users-remove-${idx}`}
                                  type="button"
                                  variant="secondary"
                                  className="h-8 rounded-full bg-white border border-slate-300 text-slate-800 hover:bg-slate-50"
                                >
                                  Remove
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent data-testid={`admin-users-remove-dialog-${idx}`} className="bg-white border-slate-200">
                                <AlertDialogHeader>
                                  <AlertDialogTitle data-testid={`admin-users-remove-title-${idx}`}>Remove user (keep audit)</AlertDialogTitle>
                                  <AlertDialogDescription data-testid={`admin-users-remove-desc-${idx}`}>
                                    This will delete the user account and revoke access, but keep all sessions for audit.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel data-testid={`admin-users-remove-cancel-${idx}`}>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    data-testid={`admin-users-remove-confirm-${idx}`}
                                    className="bg-slate-900 text-white hover:bg-slate-800"
                                    onClick={() => deleteUser(u.user_id, "keep")}
                                  >
                                    Remove
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>

                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  data-testid={`admin-users-purge-${idx}`}
                                  type="button"
                                  className="h-8 rounded-full bg-rose-600 text-white hover:bg-rose-700"
                                >
                                  Purge
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent data-testid={`admin-users-purge-dialog-${idx}`} className="bg-white border-slate-200">
                                <AlertDialogHeader>
                                  <AlertDialogTitle data-testid={`admin-users-purge-title-${idx}`}>Delete user + purge everything</AlertDialogTitle>
                                  <AlertDialogDescription data-testid={`admin-users-purge-desc-${idx}`}>
                                    This will delete the user and ALL their sessions from the app database. Drive files will remain in Google Drive.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel data-testid={`admin-users-purge-cancel-${idx}`}>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    data-testid={`admin-users-purge-confirm-${idx}`}
                                    className="bg-rose-600 text-white hover:bg-rose-700"
                                    onClick={() => deleteUser(u.user_id, "purge")}
                                  >
                                    Purge
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit User Dialog */}
        <Dialog open={!!editUser} onOpenChange={(open) => !open && closeEditDialog()}>
          <DialogContent data-testid="edit-user-dialog" className="bg-white border-slate-200 sm:max-w-md">
            <DialogHeader>
              <DialogTitle data-testid="edit-user-title">Edit user</DialogTitle>
              <DialogDescription data-testid="edit-user-desc">
                Update user details for {editUser?.email}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label data-testid="edit-user-name-label" htmlFor="edit-name">Name</Label>
                <Input
                  data-testid="edit-user-name-input"
                  id="edit-name"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="User name"
                  className="pod-input-light bg-white border-slate-300"
                />
              </div>
              <div className="space-y-2">
                <Label data-testid="edit-user-code-label" htmlFor="edit-code">User code (for filenames)</Label>
                <Input
                  data-testid="edit-user-code-input"
                  id="edit-code"
                  value={editUserCode}
                  onChange={(e) => setEditUserCode(e.target.value)}
                  placeholder="E.g. DRIVER_07 or VEHICLE_KA01"
                  className="pod-input-light bg-white border-slate-300"
                />
              </div>
              <div className="space-y-2">
                <Label data-testid="edit-user-role-label">Role</Label>
                <Select value={editRole} onValueChange={setEditRole}>
                  <SelectTrigger data-testid="edit-user-role-trigger" className="border-slate-300 bg-white text-slate-900">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent className="border-slate-200 bg-white text-slate-900">
                    <SelectItem value="user" data-testid="edit-user-role-user">User</SelectItem>
                    <SelectItem value="supervisor" data-testid="edit-user-role-supervisor">Supervisor</SelectItem>
                    <SelectItem value="admin" data-testid="edit-user-role-admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button
                data-testid="edit-user-cancel"
                type="button"
                variant="secondary"
                className="rounded-full bg-white border border-slate-300 text-slate-800 hover:bg-slate-50"
                onClick={closeEditDialog}
              >
                Cancel
              </Button>
              <Button
                data-testid="edit-user-save"
                type="button"
                disabled={saving}
                className="rounded-full bg-slate-900 text-white hover:bg-slate-800"
                onClick={saveUserEdit}
              >
                {saving ? "Saving…" : "Save changes"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
